import {Grid, Stack, Typography, Button, Switch, FormControlLabel, CircularProgress, Box} from "@mui/material";
import {useState, useEffect} from "react";
import {Company} from "../../types/domain/company";
import {KeyValuePair} from "../base/KeyValuePair";
import {useTranslation} from "react-i18next";
import {UpdateCompanyBrandingParams, updateCompanyBranding} from "../../services/companyBrandingService";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {useAdminCompanyBrandingQuery} from "../../hooks/companyBrandingHooks";
import {useMutation} from "@tanstack/react-query";
export interface Props {
    company?: Company;
    companyHierarchy?: Company[];
}

export const CompanyBrandingCard: React.FC<Props> = ({company}) => {
    const {t} = useTranslation();
    const access = useAccessMarker(ALL_ACCESS_FUNCTIONS);
    const canEditBranding = access.canEditCompanyDetails;
    const [error, setError] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isEnabled, setIsEnabled] = useState(false);

    const {data: branding, isLoading} = useAdminCompanyBrandingQuery(company?.id);

    useEffect(() => {
        if (branding) {
            setIsEnabled(branding.isEnabled ?? false);
            if (branding.logoUrl) {
                setPreviewUrl(branding.logoUrl);
            } else {
                setPreviewUrl(null);
            }
        }
    }, [branding]);

    const mutation = useMutation({
        mutationFn: (params: UpdateCompanyBrandingParams) => updateCompanyBranding(company!.id, params),
        onError: (error: Error) => {
            setError(error.message);
        },
    });

    const isSaving = mutation.isPending;

    const handleLogoUpload = async (file: File) => {
        if (!company || !canEditBranding) return;
        const maxFileSize = 5 * 1024 * 1024;
        if (file.size > maxFileSize) {
            setError(t("companies.branding.error_file_too_large"));
            return;
        }
        const tempUrl = URL.createObjectURL(file);
        setPreviewUrl(tempUrl);
        await mutation.mutateAsync({logo: file, isEnabled: branding?.isEnabled});
    };

    const handleBrandingToggle = async (newValue: boolean) => {
        if (!company || !canEditBranding) return;
        setIsEnabled(newValue);
        await mutation.mutateAsync({isEnabled: newValue});
    };
    const renderLogo = () => {
        if (isLoading) {
            return (
                <div
                    style={{
                        height: 70,
                        width: 200,
                        border: "1px dashed #ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CircularProgress size={24} />
                </div>
            );
        }

        if (previewUrl) {
            return (
                <div
                    style={{
                        height: 200,
                        width: 500,
                        border: "1px solid #eee",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f5f5f5",
                        padding: 8,
                        position: "relative",
                    }}
                >
                    <img
                        src={previewUrl}
                        alt={company?.properties.name + " logo"}
                        style={{maxWidth: "100%", maxHeight: "100%", objectFit: "contain"}}
                        key={previewUrl}
                    />
                    {isSaving && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: "rgba(255,255,255,0.7)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={24} />
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div
                style={{
                    height: 70,
                    width: 200,
                    border: "1px dashed #ccc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {t("companies.branding.no_logo")}
                </Typography>
                {isSaving && <CircularProgress size={16} style={{marginLeft: 8}} />}
            </div>
        );
    };

    if (!company) return null;

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <KeyValuePair header={t("companies.branding.logo")}>
                    <Stack direction="column" spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            {renderLogo()}
                            {canEditBranding && (
                                <>
                                    <input
                                        accept="image/*"
                                        style={{display: "none"}}
                                        id="company-logo-upload"
                                        type="file"
                                        onChange={(e) => {
                                            if (e.target.files?.[0]) {
                                                setError(null);
                                                void handleLogoUpload(e.target.files[0]);
                                                e.target.value = "";
                                            }
                                        }}
                                        disabled={isSaving}
                                    />
                                    <label htmlFor="company-logo-upload">
                                        <Button variant="outlined" component="span" size="small" disabled={isSaving}>
                                            {branding?.logoUrl
                                                ? t("companies.branding.change")
                                                : t("companies.branding.upload")}
                                        </Button>
                                    </label>
                                </>
                            )}
                        </Stack>
                        {error && (
                            <Typography variant="body2" color="error" sx={{mt: 1}}>
                                {error}
                            </Typography>
                        )}
                        {canEditBranding && (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isEnabled}
                                            onChange={(e) => void handleBrandingToggle(e.target.checked)}
                                            color="primary"
                                            disabled={isSaving || (!previewUrl && !isEnabled)}
                                        />
                                    }
                                    label={t("companies.branding.enable_branding")}
                                />
                                <Typography variant="caption" display="block" color="text.secondary" sx={{mt: 0.5}}>
                                    {t("companies.branding.enable_description")}
                                    <br />
                                    <div style={{color: "red"}}>{t("companies.branding.cascade_warning")}</div>
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </KeyValuePair>
            </Grid>
        </Grid>
    );
};
